import { KioskListing, KioskOwnerCap } from '@mysten/kiosk';
import { MIST_PER_SUI } from '@mysten/sui.js/utils';
import {
  MoveStruct,
  MoveValue,
  SuiObjectResponse,
} from '@mysten/sui.js/client';
import { normalizeSuiAddress } from '@mysten/sui.js/utils';
import { IConsts } from 'sudo-ts-sdk';
import { OwnedObjectType } from '../app/components/Inventory/OwnedObjects';
import blackCard from '../assets/scard/black-card.jpeg';
import emeraldCard from '../assets/scard/emerald-card.jpeg';
import silverCard from '../assets/scard/silver-card.jpeg';
import shadowCard from '../assets/scard/shadow-card-medium-res.png';
import emptyImage from '../assets/scard/empty-image.svg';

export const SUDO_CARD_TYPE_SUFFIX = 'card::SudoCard';
export const SUDO_MINT_TICKET_TYPE_SUFFIX = 'card::MintTicket';
export const SUDO_CARD_POINTS_TYPE_SUFFIX = 'card::Points';
export const SUDO_CARD_LOOTBOX_TYPE_SUFFIX = 'lootbox::Lootbox';
export const SUDO_CARD_LOOTBOX_WITH_AMOUNT_TYPE_SUFFIX =
  'lootbox::LootboxWithAmount';

// Parse the display of a list of objects into a simple {object_id: display} map
// to use throughout the app.
export const parseObjectDisplays = (
  data: SuiObjectResponse[],
): Record<string, Record<string, string> | undefined> => {
  return data.reduce<Record<string, Record<string, string> | undefined>>(
    (acc, item: SuiObjectResponse) => {
      const display = item.data?.display?.data;
      const id = item.data?.objectId!;
      acc[id] = display || undefined;
      return acc;
    },
    {},
  );
};

export const parseObjectFields = (
  data: SuiObjectResponse[],
): Record<string, MoveStruct> => {
  return data.reduce<Record<string, MoveStruct>>(
    (acc, item: SuiObjectResponse) => {
      if (item.data?.content?.dataType === 'moveObject') {
        const fields = item.data?.content?.fields;
        const id = item.data?.objectId;
        acc[id] = fields || undefined;
        return acc;
      }
      return acc;
    },
    {},
  );
};

export const processKioskListings = (
  data: KioskListing[],
): Record<string, KioskListing> => {
  const results: Record<string, KioskListing> = {};

  data
    .filter(x => !!x)
    .map((x: KioskListing) => {
      results[x.objectId || ''] = x;
      return x;
    });
  return results;
};

export const mistToSui = (mist: bigint | string | undefined) => {
  if (!mist) return 0;
  return Number(mist || 0) / Number(MIST_PER_SUI);
};

export const formatSui = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 5,
  }).format(amount);
};

/**
 * Finds an active owner cap for a kioskId based on the
 * address owned kiosks.
 */
export const findActiveCap = (
  caps: KioskOwnerCap[] = [],
  kioskId: string,
): KioskOwnerCap | undefined => {
  return caps.find(
    x => normalizeSuiAddress(x.kioskId) === normalizeSuiAddress(kioskId),
  );
};

export const getImage = (item: OwnedObjectType, consts: IConsts) => {
  if (item.type === `${consts.sudoNft.package}::card::MintTicket`) {
    return shadowCard;
  } else if (item.type === `${consts.sudoNft.package}::card::SudoCard`) {
    switch (item.display.tier) {
      case '0':
        return blackCard;
      case '1':
        return emeraldCard;
      case '2':
        return silverCard;
      default:
        return shadowCard;
    }
  } else {
    return emptyImage;
  }
};

export const isSudoObject = (object: any, consts: IConsts) => {
  if (
    object.type === `${consts.sudoNft.package}::card::MintTicket` ||
    object.type === `${consts.sudoNft.package}::card::SudoCard` ||
    object.type === `${consts.sudoNft.package}::card::Points` ||
    object.type === `${consts.sudoLootbox.package}::lootbox::Lootbox` ||
    object.type.includes(SUDO_CARD_LOOTBOX_WITH_AMOUNT_TYPE_SUFFIX)
  ) {
    return true;
  }
  return false;
};

export const isSudoPoints = (object: OwnedObjectType, consts: IConsts) => {
  return object.type === `${consts.sudoNft.package}::card::Points`;
};

export const extractPointsAmount = (pointFields: MoveStruct): number => {
  const castedPoints = pointFields as { fields: { [key: string]: MoveValue } };
  return Number(castedPoints.fields.amount) || 0;
};

export const formatDateForPoints = (startTimeMs: string, endTimeMs: string) => {
  const startDate = new Date(parseInt(startTimeMs, 10));
  const formattedStartDate = startDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const endDate = new Date(parseInt(endTimeMs, 10));
  const formattedEndDate = endDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return `${formattedStartDate} - ${formattedEndDate}`;
};
